import { useCallback, useEffect, useRef } from 'react'

import { FlowContextProps } from '@cash-web/shared-flow-data-access-context'
import { JourneyData } from '@cash-web/shared-journey-util-main'

type UseJourneyScrollPositionConfig = {
  active?: JourneyData
  flow?: FlowContextProps
}

/**
 * A custom hook that manages restoring scroll position when navigating between journeys.
 * When navigating back, the scroll position will be restored. When navigating forward, the scroll position will be reset to the top.
 *
 * @param active - The active journey data containing journeyId.
 * @param flow - The flow context for journey blocker state.
 * @returns A ref to the journey content element
 */
export const useJourneyScrollPosition = ({ active, flow }: UseJourneyScrollPositionConfig) => {
  // Store a reference to the journey content element
  const elementRef = useRef<HTMLElement>(null)
  const scrollElementRef = useRef<HTMLElement | null>(null)
  const wasFlowInProgressRef = useRef(false)
  const pendingBackNavigationRef = useRef(false)

  const blockerData = flow?.active?.blockerData
  const blockerStepId = blockerData?.id ?? blockerData?.blocker ?? blockerData?.type
  const nextJourneyId = active?.journeyId == null ? undefined : `${active.journeyId}:${blockerStepId ?? 'default'}`
  const isFlowInProgress = flow?.isInProgress === true

  // Store the current journey ID
  const currentJourneyRef = useRef<string | undefined>()

  // Store scroll position of each journey
  const journeyToScrollPositionRef = useRef<Record<string, number>>({})

  // Function to clear all stored scroll positions
  const clearScrollPosition = useCallback(() => {
    journeyToScrollPositionRef.current = {}
  }, [])

  // Store the scroll position of the current journey
  const onScroll = useCallback((event: Event) => {
    if (currentJourneyRef.current != null) {
      journeyToScrollPositionRef.current[currentJourneyRef.current] = (event.target as HTMLElement)?.scrollTop ?? 0
    }
  }, [])

  const markBackNavigation = useCallback(() => {
    pendingBackNavigationRef.current = true
  }, [])

  const getScrollableElement = useCallback(() => {
    const element = elementRef.current
    if (element == null) {
      return null
    }

    // Locate modal content scroll container
    const dialog = element.querySelector<HTMLElement>('[role="dialog"]')
    const modalElement = dialog ?? element

    const candidates = [modalElement, ...modalElement.querySelectorAll<HTMLElement>('*')]
    const firstScrollableElement = candidates.find(candidate => {
      const overflowY = window.getComputedStyle(candidate).overflowY
      const hasScrollableOverflow = overflowY === 'auto' || overflowY === 'scroll'
      const hasScrolled = candidate.scrollTop > 0

      return hasScrollableOverflow || hasScrolled
    })

    return firstScrollableElement ?? modalElement
  }, [])

  useEffect(() => {
    const wasFlowInProgress = wasFlowInProgressRef.current
    const hasJustExitedFlow = wasFlowInProgress && !isFlowInProgress
    wasFlowInProgressRef.current = isFlowInProgress

    const currentElement = getScrollableElement()
    if (scrollElementRef.current != null && scrollElementRef.current !== currentElement) {
      scrollElementRef.current.removeEventListener('scroll', onScroll)
    }
    scrollElementRef.current = currentElement

    const currentJourney = nextJourneyId
    const lastJourney = currentJourneyRef.current

    let isNavigatingBack = false
    const hasJourneyChanged = lastJourney !== currentJourney
    if (hasJourneyChanged && pendingBackNavigationRef.current) {
      isNavigatingBack = true
    }
    if (hasJourneyChanged) {
      pendingBackNavigationRef.current = false
    }

    // Update to the latest journey
    currentJourneyRef.current = nextJourneyId

    // If we're not going to any journey, we don't need to do anything
    if (lastJourney == null && currentJourney == null) {
      currentJourneyRef.current = undefined
      pendingBackNavigationRef.current = false
      scrollElementRef.current?.removeEventListener('scroll', onScroll)
      return
    }

    // If nextJourneyId becomes null/undefined after having a value, it likely means the journey was canceled
    // Clear all scroll positions in this case
    if (lastJourney != null && currentJourney == null) {
      clearScrollPosition()
      currentJourneyRef.current = undefined
      pendingBackNavigationRef.current = false
      scrollElementRef.current?.removeEventListener('scroll', onScroll)
      return
    }

    // If current journey is not null and we have a scroll position for it, we know that we've seen this journey before
    const hasVisitedJourneyBefore = currentJourney != null && journeyToScrollPositionRef.current[currentJourney] != null

    if (hasJustExitedFlow) {
      clearScrollPosition()
      pendingBackNavigationRef.current = false
      currentElement?.scrollTo({ top: 0 })
    } else if (isNavigatingBack && hasVisitedJourneyBefore) {
      // Restore to the last known scroll position of the journey
      currentElement?.scrollTo({ top: journeyToScrollPositionRef.current[currentJourney] })
    } else {
      // Forward navigation should always scroll to top
      currentElement?.scrollTo({ top: 0 })
    }

    // Listen for scroll events on the journey content
    currentElement?.addEventListener('scroll', onScroll)
    return () => {
      currentElement?.removeEventListener('scroll', onScroll)
    }
  }, [nextJourneyId, isFlowInProgress, clearScrollPosition, getScrollableElement, onScroll])

  return {
    ref: elementRef,
    markBackNavigation,
  }
}
